import { EventEmitter, Injectable } from '@angular/core';

import { TaskType, activity_status, sortByNumericWaypointOrder, TestSeq, DriveTest, DriveTestParticipant, DriveTestPoint, DriveTestPointSeq, DriveTestSeq } from '../models';
import { AppState } from '../app-state';

import { DriveTestDoc, driveTestSchema, driveTestMethods } from '../schema/drive-test-schema';
import { TestbedRxdbCouchCollectionService } from './testbed-rxdb/testbed-rxdb-couch-collection.service';
import { TestbedRxdbState } from './testbed-rxdb/testbed-rxdb-state';
import { AuthenticationService } from './authentication.service';
import { DriveTestMapPoint, PathTestMapPoint } from '../models/mapping'
import { PathWaypointService } from './path-waypoint.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { first } from 'rxjs/operators';
import { AppConfig } from '../../../environments/environment';
import { TestSeqService } from './test-seq.service';
import { PointTestLogService } from './point-test-log.service';
import { DriveTestSeqService } from './drive-test-seq.service';


@Injectable({ providedIn: 'root' })
export class DriveTestService extends TestbedRxdbCouchCollectionService<DriveTest,DriveTestDoc> {

    participants: DriveTestParticipant[] = AppConfig.driveTest.participants || [
        { name: 'att', title: 'AT&T'},
        { name: 'verizon', title: 'Verizon'},
        { name: 'tmobile', title: 'T-Mobile', alias: 'tmo'},
        { name: 'dish', title: 'Dish' }
      ]

    deleted$ = new EventEmitter<DriveTestDoc>()

    constructor( appState : AppState,
        authService: AuthenticationService,
        public pathWaypointService: PathWaypointService,
        public testSeqService: DriveTestSeqService,
        public logService: PointTestLogService,
        rxdbState: TestbedRxdbState ) {
        super('drive_test', driveTestSchema, appState, rxdbState, authService)

        this.methods = driveTestMethods;
        this.syncBatchSize = 50
        this.syncConfig = {
            push: true,
            pull: true,
            intervalMs: this.appState.settings.get('rxdb.collections.drive_test.intervalMs',0),
            retry: true,
            name: 'task',
            query: (c)=>c.find().where('type').eq(TaskType.DRIVE_TEST)
        }

        this.migrationStrategies = {
            1: (driveTest)=>driveTest,
            2: (driveTest)=>driveTest,
            3: (driveTest)=>driveTest
        }
    }

    findOneByTestPointId = async ( testPointId: string ) => {
        return await this.rxCollection.findOne().where( {
            testPoints: {
                $elemMatch: {id: testPointId}
            }
        }).exec()
    }


    findLatestTestPointAndSeq = (testPoints: DriveTestPoint[]) => {

        const latestTestPointSeqFn = ([latestPt, latestSeq], pt) => {
            var ptLastestSeq:   DriveTestPointSeq = null
            if (pt?.seqs?.length) {
                // find latest seq with a startTime for this test pt...
                for (var i = pt.seqs.length - 1; i >= 0 && !ptLastestSeq; i--) {
                    const s = pt.seqs[i]
                    ptLastestSeq = s?.startTime ? s : null
                }
            }
            const latestTime = latestSeq?.startTime || ''
            const ptLastestSeqTime = ptLastestSeq?.startTime || ''
            return ptLastestSeqTime?.localeCompare(latestTime) > 0 ? [pt, ptLastestSeq] : [latestPt, latestSeq]
        }
        const latestPtAndSeq = testPoints?.reduce(latestTestPointSeqFn, [null,null])
        //console.log(JSON.stringify(latestPtAndSeq))
        return latestPtAndSeq
    }


    async schedule(driveTest: any) {
        try {
            driveTest.status.scheduled = {
                timestamp: this.nowTimeStamp(),
                userId: this.appState.getUser()._id
            }

            const driveTestDoc = await this.rxCollection.findOne(driveTest._id).exec()
            if(driveTestDoc) {
                await driveTestDoc.atomicUpdate( targetDriveTest => {
                    Object.assign(targetDriveTest,driveTest)
                    return targetDriveTest
                })
            } else {
                throw new Error(`Scheduling error: Could not find drive test document by ID (building=${driveTest?._id||driveTest?.buildingId}).`)
            }
        } catch (error) {
            this.publishErrorObj(error, `Error scheduling task (${driveTest?._id||driveTest?.routeId})`, this)
        }
    }


    async stopTest(driveTestDoc: DriveTestDoc, testPoint: DriveTestPoint, seq: DriveTestPointSeq,
            dialerData: { callsPlaced: number, callsCompleted: number } = null ) {
        console.log(`stopTest(${testPoint?.routeId})`)
        console.dir(seq)
        const nowTimestamp = this.nowTimeStamp()
        const updatePointFn = ( testPoint: DriveTestPoint ) => {
            if ( !testPoint.seqs ) {
                testPoint.seqs = []
            }
            var testPointSeqIndex = testPoint.seqs?.findIndex(s=>s.id == seq.id)
            if ( testPointSeqIndex == -1 ) {
                testPoint.seqs.push(seq)
            } else {
                testPoint.seqs[testPointSeqIndex] = seq
            }
            if ( !seq.stopTime ) {
                //should have already been set by dialer-client-server while processing dialer result
                seq.stopTime = nowTimestamp
            }
            if ( dialerData ) {
                seq.data.callsPlaced = dialerData.callsPlaced
                seq.data.callsCompleted = dialerData.callsCompleted
            }
            console.log('saving seq on testPoint ' + testPoint.routeId)
            console.dir(seq)
            testPoint.statusId = activity_status.Id.COMPLETE
        }
        return await this.updateTestPoint(driveTestDoc, testPoint, updatePointFn)
    }


    updateTestPointArrayContext( mapPoints: DriveTestMapPoint[]) {
        const sortFn = (a,b)=>sortByNumericWaypointOrder(a.pathWaypoint,b.pathWaypoint)
        const startPts = mapPoints?.sort(sortFn).filter( tp => !tp.pathWaypoint?.fromPointId )
        startPts.forEach(startPt=>this.pathWaypointService.updateWaypointContext(startPt, mapPoints))
    }


    async updateTestPoint( driveTestDoc: DriveTestDoc, srcTestPoint: DriveTestPoint, updatePointFn: (tp:DriveTestPoint)=>void ) {
        try {
            const doc = await driveTestDoc.atomicUpdate( driveTest => {
                let testPoint = driveTest.testPoints.find(pt=>pt.id==srcTestPoint.id)
                if (testPoint ) {
                    updatePointFn(testPoint)
                } else {
                    this.publishError(`Point ID not found in path '${driveTestDoc.pathId}'.`,this)
                }
                return driveTest
            })
            const updatedTestPoint = doc.testPoints?.find(pt=>pt.id==srcTestPoint.id)
            return updatedTestPoint
        } catch (error) {
            console.error(`Could not update path test document ${driveTestDoc?._id} and testPoint ${srcTestPoint?.id}`)
            throw error
        }
    }

    deletePathTestModal = async ( modalService: BsModalService, driveTestDoc: DriveTestDoc ) => {

        const routeId = driveTestDoc.routeId

        let initialState = null
        let error : Error = null

        initialState = {
            onYes: async () => {
                try {
                    await this.deleteDriveTest(driveTestDoc)
                } catch ( e ) {
                    //const errorMsg = 'Delete error. ' + this.extractErrorMsg(e)
                    this.publishError(e,this)
                    error = e
                }
            },
            title: `Confirm Path Test Deletion`,
            question: `<div class="m-2 small font-italic text-danger">
                <i class='text-warning fa fa-exclamation-triangle mr-1'></i>Delete includes</div>`
                + `<div class="pt-2">Delete Drive Test for ${routeId} (cycle ${driveTestDoc.cycle})?</div>`,
            focusOnNo: true,
            yesStyle: 'btn btn-danger'
        }
        const modalRef = modalService.show(ConfirmModalComponent, {
            ignoreBackdropClick: true,
            id: 202111011,
            initialState
        });
        await modalRef.onHidden.pipe(first()).toPromise()
        if ( error ) {
            initialState = {
                yesBtnText: null,
                noBtnText: 'OK',
                title: `Error Deleting Drive Test`,
                question: `<div class="alert alert-danger text-left">
                    <i class='fa fa-exclamation-triangle mr-2'></i>${error.message}</div>`,
                focusOnNo: true
            }
            const modalRef = modalService.show(ConfirmModalComponent, {
                ignoreBackdropClick: true,
                id: 202111012,
                initialState
            });
            await modalRef.onHidden.pipe(first()).toPromise()
            throw error
        }
    }

    deleteDriveTest = async (driveTestDoc:DriveTestDoc) => {
        console.log(`delete() driveTestDoc.routeId=${driveTestDoc.routeId}, driveTestDoc._id=${driveTestDoc._id}`)
        await driveTestDoc.remove()
        this.deleted$.emit(driveTestDoc)
    }

    export = async ( doc: DriveTestDoc ) => {
        const withRevAndAttachments = true
        let bWarnMissingDialerData = false
        const driveTest = doc.toJSON(withRevAndAttachments)
        const seqs : any = []
        for ( const testPoint of driveTest?.testPoints ) {
            const pointSeq: any = { testPointId: testPoint.id, testSeqs: [] }
            for( const seq of testPoint?.seqs) {
                if ( seq.dialerSeqId ) {
                    let testSeq = (await this.testSeqService.rxCollection.findOne(seq.dialerSeqId).exec())?.toJSON(withRevAndAttachments) as DriveTestSeq
                    if ( !testSeq || (!testSeq.dialer && testSeq.endTime) ) {
                        if ( this.appState.isOffline() ) {
                            bWarnMissingDialerData = true
                        } else {
                            try {
                                const serverTestSeq = await this.testSeqService.getByIdViaRest(seq.dialerSeqId)
                                if ( serverTestSeq ) {
                                    if ( !testSeq ) {
                                        testSeq = serverTestSeq
                                    } else {
                                        testSeq.dialer = serverTestSeq.dialer
                                        if ( serverTestSeq.updatedAt != testSeq.updatedAt ) {
                                            console.warn(`Export testSeq dialer data updatedAt mismatch (local: ${testSeq.updatedAt}, server: ${serverTestSeq.updatedAt}`)
                                        }
                                    }
                                }
                            } catch ( error: any ) {
                                bWarnMissingDialerData = true
                                this.testSeqService.publishError(error,this)
                            }
                        }
                    }
                    if ( testSeq ) {
                        pointSeq.testSeqs.push( testSeq )
                    }
                }
                pointSeq.comments = (await this.logService.rxCollection.find().where('taskId').eq(seq.id).exec())?.map(doc=>doc?.toJSON(withRevAndAttachments))
            }
            seqs.push(pointSeq)
        }
        if ( bWarnMissingDialerData ) {
            this.appState.notifyWarning(
                `Drive Test Export is missing dialer data only on server${this.appState.isOffline()?' (offline)':''}`,this,15)
        }
        const strJson = JSON.stringify( {
            driveTest,
            seqs
        },null,2)
        const blob = new Blob([strJson], {type: 'application/json'})
        saveAs(blob, `DriveTest_${driveTest.buildingId}_${driveTest.pathId}_${driveTest.stage}_${driveTest.cycle}.json`);
    }

}